import {Button, DataStatus} from 'platform/components';
import {Box, Right, Space, Stack, VStack, Hide, Show} from 'platform/foundation';

// eslint-disable-next-line no-restricted-imports -- [T20-75547]
import {useLocation, useNavigate} from 'react-router-dom';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {suffixTestId, composePath, TestIdProps} from 'shared/web';

import {useGeneralVehicleParameters} from '../../hooks/useGeneralVehicleParameters';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {queryParams} from '../../utils/queryParams';
import {CommentsWidget} from '../CommentsWidget/CommentsWidget';
import {VehicleNoteWidget} from '../VehicleNoteWidget/VehicleNoteWidget';
import {VehicleInfoCard} from './components/VehicleInfoCard';
import {useEngineVehicleParameters} from './hooks/useEngineVehicleParameters';
import {useFuelVehicleParameters} from './hooks/useFuelVehicleParameters';
import {useServiceInspectionParameters} from './hooks/useServiceInspectionParameters';
import {useSpecificationVehicleParameters} from './hooks/useSpecificationVehicleParameters';
import {useTechnicalInspectionParameters} from './hooks/useTechnicalInspectionParameters';
import {useWarrantyAndValidityVehicleParameters} from './hooks/useWarrantyAndValidityVehicleParameters';

interface VehicleOverviewProps extends TestIdProps {
  vehicleId: string;
  isDetailButtonHidden?: boolean;
  isEditingDisabled?: boolean;
}

export function VehicleOverview(props: VehicleOverviewProps) {
  const navigate = useNavigate();
  const {pathname, search} = useLocation();

  const {
    data: vehicleData,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery({
    vehicleId: props.vehicleId,
  });

  const generalParameters = useGeneralVehicleParameters(props.vehicleId);
  const specificationParameters = useSpecificationVehicleParameters(props.vehicleId);
  const fuelParameters = useFuelVehicleParameters(props.vehicleId);
  const engineParameters = useEngineVehicleParameters(props.vehicleId);
  const warrantyAndValidityParameters = useWarrantyAndValidityVehicleParameters(props.vehicleId);
  const technicalInspectionParameters = useTechnicalInspectionParameters(props.vehicleId);
  const serviceInspectionParameters = useServiceInspectionParameters(props.vehicleId);

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const [
    hasVehicleCommentReadPermission,
    hasVehicleCommentAddPermission,
    hasVehicleCommentDeletePermission,
  ] = usePermissions({
    permissionKeys: ['vehicleCommentRead', 'vehicleCommentAdd', 'vehicleCommentDelete'],
    scopes: {
      vehicleCommentRead: {participation: vehicleParticipation, branchId: vehicleData?.branchId},
      vehicleCommentAdd: {participation: vehicleParticipation, branchId: vehicleData?.branchId},
      vehicleCommentDelete: {participation: vehicleParticipation, branchId: vehicleData?.branchId},
    },
  });

  const handleNavigateWithHash = (hash: string) => {
    const searchParams = new URLSearchParams({[queryParams.RETURN_URL]: pathname + search});

    const tab = hash === 'info' ? 'basic-information' : 'vehicle-data';
    return navigate({
      pathname: composePath(vehiclesRoutes.editV2Tab, {
        params: {
          id: props.vehicleId,
          tab,
        },
        isSearchParamsPreserved: false,
      }),
      search: `?${searchParams.toString()}`,
    });
  };

  const handleNavigateVehicleData = () => {
    const searchParams = new URLSearchParams({[queryParams.RETURN_URL]: pathname + search});

    return navigate({
      pathname: composePath(vehiclesRoutes.editV2Tab, {
        params: {
          id: props.vehicleId,
          tab: 'vehicle-data',
        },
        isSearchParamsPreserved: false,
      }),
      search: `?${searchParams.toString()}`,
    });
  };

  return (
    <DataStatus isLoading={isVehicleLoading} isError={isVehicleError} minHeight={100}>
      <Hide when={props.isDetailButtonHidden}>
        <Right>
          <Button
            variant="outlined"
            leftIcon="action/launch"
            title={i18n.t('entity.vehicle.labels.vehicleDetails')}
            data-testid={suffixTestId('vehicleDetails', props)}
            onClick={() =>
              window.open(
                composePath(vehiclesRoutes.detail, {params: {id: props.vehicleId}}),
                '_blank'
              )
            }
          />
        </Right>
        <Space vertical={4} />
      </Hide>
      <Stack direction={['column', 'column', 'column', 'row']} spacing={4}>
        <Box flex={1}>
          <VStack spacing={4}>
            <VehicleInfoCard
              title={i18n.t('entity.vehicle.labels.generalInformation')}
              onEditClick={() => handleNavigateWithHash('info')}
              parameters={generalParameters}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('general-info', props)}
            />
            <VehicleInfoCard
              title={i18n.t('entity.vehicle.labels.modelSpecification')}
              onEditClick={() => handleNavigateWithHash('model')}
              parameters={specificationParameters}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('model-specification', props)}
            />
            <VehicleInfoCard
              title={i18n.t('entity.vehicle.labels.fuel')}
              onEditClick={() => handleNavigateWithHash('fuel')}
              parameters={fuelParameters}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('fuel', props)}
            />
            <VehicleInfoCard
              title={i18n.t('entity.vehicle.labels.engine')}
              onEditClick={() => handleNavigateWithHash('engine')}
              parameters={engineParameters}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('engine', props)}
            />
          </VStack>
        </Box>
        <Box flex={1}>
          <VStack spacing={4}>
            <VehicleInfoCard
              title={i18n.t('entity.vehicle.labels.warrantyAndValidity')}
              onEditClick={() => handleNavigateVehicleData()}
              parameters={warrantyAndValidityParameters}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('warranty-and-validity', props)}
            />
            <VehicleInfoCard
              title={i18n.t('entity.vehicle.labels.technicalInspection')}
              onEditClick={() => handleNavigateVehicleData()}
              parameters={technicalInspectionParameters}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('technical-inspection', props)}
            />
            <VehicleInfoCard
              title={i18n.t('entity.vehicle.labels.serviceInspection')}
              onEditClick={() => handleNavigateVehicleData()}
              parameters={serviceInspectionParameters}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('service-inspections', props)}
            />
            <VehicleNoteWidget
              vehicleId={props.vehicleId}
              title={i18n.t('entity.vehicle.labels.vehicleAdditionalNotes')}
            />
            <Show when={hasVehicleCommentReadPermission}>
              <CommentsWidget
                title={i18n.t('entity.vehicle.labels.vehicleComments')}
                resourceId={EntityResourceIds.vehicle}
                recordId={props.vehicleId}
                isAddDisabled={!hasVehicleCommentAddPermission}
                isDeleteDisabled={!hasVehicleCommentDeletePermission}
              />
            </Show>
          </VStack>
        </Box>
      </Stack>
    </DataStatus>
  );
}
